import React, { useState, useMemo, useCallback } from 'react';
import BacktestEquityDisplay from '../BacktestEquityDisplay';
import './BacktestProgress.css';

const DEFAULT_BACKTEST_STARTING_VALUE = 100000;
const TERMINAL_STATUSES = new Set([
  'completed',
  'completed_no_trades',
  'completed_with_errors',
  'failed',
  'force_stopped',
  'terminated_timeout',
  'not_found',
]);

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const firstFiniteNumber = (values) => {
  for (const value of values) {
    const numeric = toFiniteNumber(value);
    if (numeric !== null) return numeric;
  }
  return null;
};

/**
 * BacktestProgress - Unified progress component for backtests
 *
 * Features:
 * - Main progress bar with simulation date
 * - Download sub-progress (collapsible)
 * - Recharts-based portfolio chart
 * - Enhanced tooltip with positions
 * - Benchmark toggle (S&P 500)
 *
 * @param {Object} progress - Current backtest progress data from backend
 * @param {Array} chartData - Array of data points for the chart
 * @param {boolean} isStarting - Whether backtest is in initialization phase
 * @param {string} stage - Optional backend stage string (e.g., waiting_for_capacity)
 * @param {string} status - Optional backend status string (fallback when stage absent)
 * @param {boolean} showBenchmarkToggle - Show benchmark checkbox
 * @param {boolean} showBenchmark - Current benchmark visibility state
 * @param {Function} onBenchmarkToggle - Callback when benchmark toggled
 * @param {string} startDate - Optional start date of the backtest period (e.g., "2016-01-04" or "Jan 4, 2016")
 * @param {string} endDate - Optional end date of the backtest period (e.g., "2026-01-17" or "Jan 17, 2026")
 * @param {number|string} version - Optional strategy version number to display
 */
export default function BacktestProgress({
  progress,
  chartData = [],
  isStarting = false,
  stage,
  status,
  showBenchmarkToggle = true,
  showBenchmark = false,
  onBenchmarkToggle,
  benchmarkData = [],
  elapsedTime,
  startDate = null,
  endDate = null,
  version = null,
  revisionId = null,
  strategyParameters = null,
  routingPolicySnapshot = null,
}) {
  const [showProgressTooltip, setShowProgressTooltip] = useState(false);
  const [showDownloadTooltip, setShowDownloadTooltip] = useState(false);

  const startingStageKey = useMemo(() => {
    const raw = stage || status;
    return typeof raw === 'string' ? raw.toLowerCase() : '';
  }, [stage, status]);

  const startingMessage = useMemo(() => {
    if (!startingStageKey) {
      return 'Initializing strategy and preparing backtest environment...';
    }

    switch (startingStageKey) {
      case 'waiting_for_capacity':
        return 'Waiting for Capacity...';
      case 'queued':
        return 'Queued...';
      case 'building':
      case 'pulling':
        return 'Preparing backtest environment...';
      case 'deploying':
      case 'starting':
      case 'booting':
      case 'initiated':
      case 'initiating':
        return 'Starting backtest...';
      default:
        return 'Initializing strategy and preparing backtest environment...';
    }
  }, [startingStageKey]);

  // Helper to get asset display name from download status
  const getAssetDisplayName = (downloadStatus) => {
    if (!downloadStatus?.asset) return '';

    // If asset is a string, return it directly
    if (typeof downloadStatus.asset === 'string') {
      return downloadStatus.asset;
    }

    // If asset is an object, try to construct the full symbol
    const asset = downloadStatus.asset;

    // For options: check asset.type === "option" and has strike/exp/right
    if (asset.type === 'option' && asset.strike && asset.exp && asset.right) {
      // Format: "SPY 685C 12/23/25"
      const rightShort = asset.right === 'CALL' ? 'C' : asset.right === 'PUT' ? 'P' : asset.right;
      // Format expiration: "2025-12-23" → "12/23/25"
      const [year, month, day] = asset.exp.split('-');
      const expFormatted = `${month}/${day}/${year.slice(2)}`;
      return `${asset.symbol} ${asset.strike}${rightShort} ${expFormatted}`;
    }

    // For stocks or other types, just return the symbol
    return asset.symbol || JSON.stringify(asset);
  };

  // Parse download_status from progress.download_status (JSON string)
  const downloadStatus = useMemo(() => {
    if (
      [status, stage].some(
        (value) => typeof value === 'string' && TERMINAL_STATUSES.has(value.toLowerCase()),
      )
    ) {
      return null;
    }
    if (!progress?.download_status) return null;

    try {
      const parsed =
        typeof progress.download_status === 'string'
          ? JSON.parse(progress.download_status)
          : progress.download_status;

      // Only return if active and has meaningful data
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        return parsed;
      }
    } catch (e) {
      // Invalid JSON, ignore
    }

    return null;
  }, [progress?.download_status, status, stage]);

  const providerWaitMessage = useMemo(() => {
    if (
      downloadStatus?.active !== true ||
      downloadStatus?.provider_wait?.classification !== 'rate_limited'
    ) {
      return null;
    }
    return downloadStatus.provider_wait.provider === 'ibkr'
      ? 'Waiting for Interactive Brokers to allow the next data request...'
      : 'Waiting for the data provider to allow the next request...';
  }, [downloadStatus]);

  // Handle benchmark toggle
  const handleBenchmarkToggle = useCallback(
    (e) => {
      if (onBenchmarkToggle) {
        onBenchmarkToggle(e.target.checked);
      }
    },
    [onBenchmarkToggle],
  );

  /**
   * Format duration in a human-readable way
   * Examples: "2 seconds", "45 seconds", "3 minutes", "2 hours, 15 minutes", "1 day, 3 hours"
   */
  const formatDuration = useCallback((totalSeconds) => {
    if (typeof totalSeconds !== 'number' || isNaN(totalSeconds) || totalSeconds < 0) {
      return null;
    }

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    const parts = [];

    if (days > 0) {
      parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    }
    if (hours > 0) {
      parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    if (minutes > 0 && days === 0) {
      // Only show minutes if less than a day
      parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }
    if (seconds > 0 && days === 0 && hours === 0) {
      // Only show seconds if less than an hour
      parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
    }

    // Handle zero case
    if (parts.length === 0) {
      return '0 seconds';
    }

    // Join with comma, but limit to 2 most significant parts for readability
    return parts.slice(0, 2).join(', ');
  }, []);

  /**
   * Parse ETA string (could be seconds, "HH:MM:SS", or already formatted) and format nicely
   */
  const formatEta = useCallback(
    (eta) => {
      if (!eta || eta === '') return null;

      // If it's a number (seconds), format it
      if (typeof eta === 'number') {
        return formatDuration(eta);
      }

      // If it's a string that looks like a number, parse and format
      const numericEta = parseFloat(eta);
      if (!isNaN(numericEta) && /^[\d.]+$/.test(eta.trim())) {
        return formatDuration(numericEta);
      }

      // If it's in "HH:MM:SS" or "MM:SS" format, parse and format
      const timeMatch = eta.match(/^(\d+):(\d{2})(?::(\d{2}))?$/);
      if (timeMatch) {
        let totalSeconds;
        if (timeMatch[3] !== undefined) {
          // HH:MM:SS format
          totalSeconds =
            parseInt(timeMatch[1]) * 3600 + parseInt(timeMatch[2]) * 60 + parseInt(timeMatch[3]);
        } else {
          // MM:SS format
          totalSeconds = parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2]);
        }
        return formatDuration(totalSeconds);
      }

      // Return as-is if we can't parse it (already formatted or unknown format)
      return eta;
    },
    [formatDuration],
  );

  // Format elapsed time - now uses human-readable format
  const formatElapsed = useCallback(
    (seconds) => {
      if (typeof seconds !== 'number') {
        // Try to parse progress.elapsed if it exists
        if (progress?.elapsed) {
          return formatEta(progress.elapsed);
        }
        return '0 seconds';
      }
      return formatDuration(seconds);
    },
    [progress?.elapsed, formatDuration, formatEta],
  );

  // Render initializing state
  if (isStarting && !progress) {
    return (
      <div className="backtest-progress-container initializing">
        <div className="progress-header">
          <h3 className="progress-title">Starting Backtest</h3>
        </div>
        <div
          className="bp-progress-wrapper"
          data-testid="indeterminate-progress"
          role="progressbar"
          aria-busy="true"
          aria-label="Initializing backtest"
        >
          <div className="bp-progress-track indeterminate">
            <div className="bp-progress-fill-indeterminate" />
          </div>
        </div>
        <div className="progress-message">{startingMessage}</div>
      </div>
    );
  }

  // Main render
  // Support both 'percent' and 'pct' field names from backend
  const percent = progress?.percent ?? progress?.pct ?? 0;
  const percentNum = typeof percent === 'string' ? parseFloat(percent) : percent;

  const firstChartPoint = chartData?.[0] || null;
  const startingPortfolioValue = firstFiniteNumber([
    firstChartPoint?.y,
    firstChartPoint?.value,
    firstChartPoint?.portfolioValue,
    progress?.initial_portfolio_value,
    progress?.initialPortfolioValue,
    progress?.initial_value,
    progress?.initialValue,
    progress?.starting_cash,
    progress?.startingCash,
    progress?.starting_balance,
    progress?.startingBalance,
    DEFAULT_BACKTEST_STARTING_VALUE,
  ]);
  const runningResult = {
    startDate,
    endDate,
    version,
    initialPortfolioValue: startingPortfolioValue,
  };
  const hasBenchmarkData = Boolean(
    chartData?.some((point) => point?.benchmark !== null && point?.benchmark !== undefined) ||
    benchmarkData?.length > 0,
  );
  const parameterEntries = Object.entries(strategyParameters?.values || {});
  const simulatedDate = progress?.simulation_date || progress?.simulationDate || null;
  const simulatedDateLabel = simulatedDate
    ? new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${String(simulatedDate).slice(0, 10)}T00:00:00Z`))
    : null;
  const policySelection = routingPolicySnapshot?.selection || 'BotSpot Auto';

  return (
    <div className="backtest-progress-container">
      <section className="bp-run-status" aria-label="Running backtest status">
        <div className="bp-run-status__headline">
          <span className="bp-run-status__pulse" aria-hidden />
          <strong>Backtest running</strong>
          <span>{Number(percentNum.toFixed(1))}% complete</span>
        </div>
        <div className="bp-run-status__facts">
          {simulatedDateLabel && (
            <span>
              Simulated through <strong>{simulatedDateLabel}</strong>
            </span>
          )}
          <span>
            Elapsed <strong>{formatElapsed(elapsedTime)}</strong>
          </span>
          {(version || revisionId) && (
            <span title={revisionId || undefined}>
              Revision <strong>{version ? `v${version}` : String(revisionId).slice(0, 8)}</strong>
            </span>
          )}
        </div>
        {(parameterEntries.length > 0 || routingPolicySnapshot) && (
          <div className="bp-run-status__context">
            {parameterEntries.length > 0 && (
              <div className="bp-run-status__parameters" aria-label="Running strategy parameters">
                {parameterEntries.map(([key, value]) => (
                  <span key={key}>
                    <b>{key}</b> <code>{String(value)}</code>
                  </span>
                ))}
              </div>
            )}
            {routingPolicySnapshot && (
              <div className="bp-run-status__routing">
                <span>
                  Selected: <strong>{policySelection}</strong>
                </span>
                {routingPolicySnapshot.version && (
                  <span>
                    Policy <strong>{routingPolicySnapshot.version}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </section>
      <BacktestEquityDisplay
        mode="running"
        points={chartData}
        progress={progress}
        progressPercent={percentNum}
        result={runningResult}
        showBenchmark={showBenchmark && hasBenchmarkData}
        height={260}
        className="bp-equity-display"
      />

      {/* Main Progress Bar - Full Width with Hover Tooltip */}
      <div
        className="bp-progress-wrapper"
        role="progressbar"
        aria-valuenow={percentNum}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-label={`Backtest progress: ${percentNum.toFixed(1)}% complete`}
        onMouseEnter={() => setShowProgressTooltip(true)}
        onMouseLeave={() => setShowProgressTooltip(false)}
      >
        <div className="bp-progress-track">
          <div
            className="bp-progress-fill"
            data-testid="progress-fill"
            style={{ width: `${Math.max(percentNum, 0)}%` }}
          />
        </div>
        {/* Hover tooltip with stats */}
        {showProgressTooltip && (
          <div className="bp-progress-tooltip">
            <span className="tooltip-stat">{percentNum.toFixed(1)}% complete</span>
            {progress?.eta && progress.eta !== '' && formatEta(progress.eta) && (
              <span className="tooltip-stat">ETA: {formatEta(progress.eta)}</span>
            )}
            <span className="tooltip-stat">Elapsed: {formatElapsed(elapsedTime)}</span>
          </div>
        )}
      </div>

      {/* Download Status - Simple, non-collapsible */}
      {downloadStatus && (
        <div
          className="download-status-section"
          data-testid="download-status-section"
          onMouseEnter={() => setShowDownloadTooltip(true)}
          onMouseLeave={() => setShowDownloadTooltip(false)}
        >
          {/* Case 1: Active ThetaData download with progress */}
          {downloadStatus.active &&
            downloadStatus.asset &&
            (downloadStatus.progress !== undefined || providerWaitMessage) && (
              <div className="download-status-active">
                <span className="download-icon-arrow"></span>
                <span className="download-text">
                  {providerWaitMessage || (
                    <>
                      Downloading {getAssetDisplayName(downloadStatus)}
                      {downloadStatus.data_type && ` (${downloadStatus.data_type})`}
                      {' • '}
                      <strong>{downloadStatus.progress}%</strong>
                      {downloadStatus.queue_position && (
                        <span className="download-queue">
                          {' '}
                          • Queue: {downloadStatus.queue_position}
                        </span>
                      )}
                      {downloadStatus.estimated_wait && (
                        <span className="download-wait">
                          {' '}
                          • Wait: {downloadStatus.estimated_wait.toFixed(1)}s
                        </span>
                      )}
                    </>
                  )}
                </span>

                {/* Tooltip on hover */}
                {showDownloadTooltip && (
                  <div className="download-tooltip">
                    <div className="tooltip-title">
                      {providerWaitMessage ? 'Waiting for Market Data' : 'Downloading Market Data'}
                    </div>
                    <div className="tooltip-desc">
                      {providerWaitMessage ? (
                        'The data provider has temporarily limited requests. Your backtest is waiting to retry; simulation progress has not advanced because of this wait.'
                      ) : (
                        <>
                          We're currently downloading {downloadStatus.data_type || 'price'} data for{' '}
                          {getAssetDisplayName(downloadStatus)} to run your backtest simulation.
                          {downloadStatus.queue_position > 1 && (
                            <> Your request is in queue position {downloadStatus.queue_position}.</>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

          {/* Case 2: Bootstrap status (initialization) */}
          {downloadStatus.active &&
            !downloadStatus.asset &&
            (downloadStatus.stage || downloadStatus.message) && (
              <div className="download-status-bootstrap">
                <span className="download-icon">⏳</span>
                <span className="download-text">
                  {providerWaitMessage || downloadStatus.message || downloadStatus.stage}
                </span>
              </div>
            )}
        </div>
      )}

      {/* Benchmark Toggle */}
      {showBenchmarkToggle && (
        <label className="benchmark-toggle">
          <input
            type="checkbox"
            checked={showBenchmark}
            onChange={handleBenchmarkToggle}
            aria-label="Show S&P 500 Benchmark"
          />
          <span className="toggle-text">Show S&P 500 Benchmark</span>
        </label>
      )}
    </div>
  );
}
